import React, {
  useMemo,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';

/**
 * Validate a positive whole-number identifier.
 */
function hasValidId(value) {
  const numericValue =
    Number(value);

  return (
    Number.isInteger(
      numericValue
    ) &&
    numericValue > 0
  );
}

/**
 * Clinical-note PDF document actions.
 *
 * This component provides access to:
 *
 * - the finalized PDF for the current clinical note
 * - the complete authorization PDF packet
 * - the complete clinical-episode PDF packet
 *
 * The backend remains the source of truth for packet eligibility.
 *
 * For example:
 *
 * - individual notes must be finalized before their PDF can be created
 * - authorization packets enforce authorization-level packet rules
 * - episode packets include finalized documentation across
 *   authorization boundaries
 * - Initial Evaluation / Re-Evaluation following-Daily requirements
 *   are enforced by the backend packet services
 *
 * This component never tries to reproduce those clinical/document
 * rules in the browser.
 */
export default function ClinicalNotePdfPacketPanel({
  clinicalNoteId,
  authId,
  clinicalEpisodeId,
  signed = false,
  enabled = true,
}) {
  const [
    activeDownload,
    setActiveDownload,
  ] = useState('');

  const [
    error,
    setError,
  ] = useState('');

  /**
   * Determine which identifiers are actually available.
   */
  const hasClinicalNoteId =
    useMemo(
      () =>
        hasValidId(
          clinicalNoteId
        ),
      [clinicalNoteId]
    );

  const hasAuthorizationId =
    useMemo(
      () =>
        hasValidId(
          authId
        ),
      [authId]
    );

  const hasClinicalEpisodeId =
    useMemo(
      () =>
        hasValidId(
          clinicalEpisodeId
        ),
      [clinicalEpisodeId]
    );

  const busy =
    Boolean(
      activeDownload
    );

  /**
   * Download the finalized PDF for the current clinical note.
   */
  async function handleDownloadNotePdf() {
    if (
      !enabled ||
      busy ||
      !signed ||
      !hasClinicalNoteId
    ) {
      return;
    }

    setActiveDownload(
      'note'
    );

    setError('');

    try {
      await clinicalNoteService
        .downloadPdf(
          clinicalNoteId
        );
    } catch (downloadError) {
      setError(
        downloadError?.message ||
          'The clinical-note PDF could not be downloaded.'
      );
    } finally {
      setActiveDownload('');
    }
  }

  /**
   * Download the complete insurer-facing packet for the
   * authorization connected to this clinical note.
   *
   * Eligibility is deliberately determined by the backend.
   */
  async function handleDownloadAuthorizationPacket() {
    if (
      !enabled ||
      busy ||
      !hasAuthorizationId
    ) {
      return;
    }

    setActiveDownload(
      'authorization'
    );

    setError('');

    try {
      await clinicalNoteService
        .downloadAuthorizationPdfPacket(
          authId
        );
    } catch (downloadError) {
      setError(
        downloadError?.message ||
          'The authorization PDF packet could not be downloaded.'
      );
    } finally {
      setActiveDownload('');
    }
  }

  /**
   * Download all finalized documentation belonging to the
   * complete clinical episode.
   *
   * The episode packet may cross multiple authorizations.
   */
  async function handleDownloadEpisodePacket() {
    if (
      !enabled ||
      busy ||
      !hasClinicalEpisodeId
    ) {
      return;
    }

    setActiveDownload(
      'episode'
    );

    setError('');

    try {
      await clinicalNoteService
        .downloadEpisodePdfPacket(
          clinicalEpisodeId
        );
    } catch (downloadError) {
      setError(
        downloadError?.message ||
          'The clinical-episode PDF packet could not be downloaded.'
      );
    } finally {
      setActiveDownload('');
    }
  }

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gap: 14,
        }}
      >
        {/* =====================================================
            CURRENT CLINICAL NOTE
            ===================================================== */}
        <div
          style={{
            border:
              '1px solid #ddd',
            borderRadius: 8,
            padding: 14,
            background: '#fff',
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              marginBottom: 5,
              color: '#222',
            }}
          >
            Current Clinical Note
          </div>

          <div
            style={{
              fontSize: 13,
              color: '#666',
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            Download only the finalized document for this
            date of service.
          </div>

          <button
            type="button"
            onClick={
              handleDownloadNotePdf
            }
            disabled={
              !enabled ||
              busy ||
              !signed ||
              !hasClinicalNoteId
            }
          >
            {activeDownload ===
            'note'
              ? 'Preparing PDF…'
              : 'Download This Note PDF'}
          </button>

          {!signed && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: '#777',
              }}
            >
              The individual PDF becomes available after
              the clinical note is permanently signed.
            </div>
          )}
        </div>

        {/* =====================================================
            AUTHORIZATION PACKET
            ===================================================== */}
        <div
          style={{
            border:
              '1px solid #ddd',
            borderRadius: 8,
            padding: 14,
            background: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: 5,
            }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#222',
              }}
            >
              Authorization Packet
            </div>

            {hasAuthorizationId && (
              <span
                style={{
                  fontSize: 12,
                  color: '#666',
                }}
              >
                Authorization #{authId}
              </span>
            )}
          </div>

          <div
            style={{
              fontSize: 13,
              color: '#666',
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            Download the insurer-facing clinical documentation
            associated with this authorization.
          </div>

          <button
            type="button"
            onClick={
              handleDownloadAuthorizationPacket
            }
            disabled={
              !enabled ||
              busy ||
              !hasAuthorizationId
            }
          >
            {activeDownload ===
            'authorization'
              ? 'Preparing Packet…'
              : 'Download Authorization Packet'}
          </button>

          {!hasAuthorizationId && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: '#777',
              }}
            >
              An authorization ID is not available for this
              clinical note.
            </div>
          )}

          {hasAuthorizationId && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: '#777',
              }}
            >
              Packet readiness is verified by the backend before
              the PDF is generated.
            </div>
          )}
        </div>

        {/* =====================================================
            COMPLETE CLINICAL EPISODE
            ===================================================== */}
        <div
          style={{
            border:
              '1px solid #ddd',
            borderRadius: 8,
            padding: 14,
            background: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: 5,
            }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: '#222',
              }}
            >
              Full Episode Packet
            </div>

            {hasClinicalEpisodeId && (
              <span
                style={{
                  fontSize: 12,
                  color: '#666',
                }}
              >
                Episode #{clinicalEpisodeId}
              </span>
            )}
          </div>

          <div
            style={{
              fontSize: 13,
              color: '#666',
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            Download finalized clinical documentation across
            the complete treatment episode, including documents
            from multiple authorizations when applicable.
          </div>

          <button
            type="button"
            onClick={
              handleDownloadEpisodePacket
            }
            disabled={
              !enabled ||
              busy ||
              !hasClinicalEpisodeId
            }
          >
            {activeDownload ===
            'episode'
              ? 'Preparing Episode…'
              : 'Download Full Episode Packet'}
          </button>

          {!hasClinicalEpisodeId && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: '#777',
              }}
            >
              This clinical note is not currently connected to
              a clinical episode.
            </div>
          )}

          {hasClinicalEpisodeId && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: '#777',
              }}
            >
              Only permanently finalized documentation is
              included in the episode packet.
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          DOWNLOAD ERROR
          ===================================================== */}
      {error && (
        <div
          role="alert"
          style={{
            marginTop: 14,
            border:
              '1px solid #c62828',
            background:
              '#fff5f5',
            padding: 12,
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          <strong>
            PDF download unavailable
          </strong>

          <div
            style={{
              marginTop: 4,
            }}
          >
            {error}
          </div>
        </div>
      )}
    </div>
  );
}